import type { Focus } from '@goaltree/backend/convex/lib/focus'
import { cn } from '@goaltree/ui/lib/utils'
import { ChevronRight, Flame, Layers, Mountain } from 'lucide-react'
import { useTranslation } from 'react-i18next'

const CARDS = [
  { focus: 'habits', icon: Flame, color: '#FF8A3D', desc: true },
  { focus: 'goals', icon: Mountain, color: '#3DA9FC', desc: true },
  { focus: 'both', icon: Layers, color: '#A66BFF', desc: false },
] as const

// Onboarding step 1: "What brings you here?"
export function FocusStep({
  selected,
  onPick,
}: {
  selected?: Focus | null
  onPick: (f: Focus) => void
}) {
  const { t } = useTranslation()
  return (
    <div className="mx-auto flex w-full max-w-lg flex-col gap-6 pt-6 pb-12 lg:pt-12">
      <div className="space-y-1">
        <h1 className="text-2xl font-black tracking-tight sm:text-3xl">
          {t('onboarding.focus.title')}
        </h1>
        <p className="text-sm text-muted-foreground">{t('onboarding.focus.subtitle')}</p>
      </div>
      <div className="flex flex-col gap-2.5">
        {CARDS.map(({ focus, icon: Icon, color, desc }) => {
          const on = selected === focus
          return (
            <button
              key={focus}
              type="button"
              aria-pressed={on}
              onClick={() => onPick(focus)}
              className={cn(
                'group flex cursor-pointer items-center gap-4 rounded-2xl border p-4 text-left backdrop-blur-sm transition-all hover:scale-[1.01] active:scale-[0.99]',
                on
                  ? 'border-primary/70 bg-primary/10'
                  : 'border-border/60 bg-card/40 hover:border-border',
              )}
            >
              <span
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl"
                style={{ background: `${color}22`, color }}
              >
                <Icon className="h-5 w-5" strokeWidth={2.2} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-base leading-tight font-semibold text-foreground">
                  {t(`onboarding.focus.${focus}`)}
                </span>
                {desc && (
                  <span className="mt-0.5 block text-sm text-muted-foreground">
                    {t(`onboarding.focus.${focus}Desc`)}
                  </span>
                )}
              </span>
              <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/60 transition-transform group-hover:translate-x-0.5" />
            </button>
          )
        })}
      </div>
    </div>
  )
}
